import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ReplayIcon from '@mui/icons-material/Replay'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { useSnackbar } from 'notistack'
import { documentoService } from '@/services/documentoService'
import { documentoReusoService } from '@/services/documentoReusoService'
import { estruturacaoConsultorService } from '@/services/estruturacaoConsultorService'
import { checklistReservaService, mensagemErroChecklistReserva } from '@/services/checklistReservaService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import { MENSAGEM_POR_CODIGO, mensagemErroDocumento } from '@/utils/mensagemErroDocumento'
import {
  STATUS_JANELA_CONSULTOR,
  chaveDoGrupo,
  ehSolicitacaoCuradoria,
  gruposComItens,
  rotuloDoGrupo,
  rotuloDoItem,
  statusDoItem,
  temBasicoFaltando,
  temItemEmVerificacao,
} from '@/utils/checklistReserva'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { agruparArquivosPorItem } from '@/components/documentos/ChecklistComArquivos'
import {
  CabecalhoVersaoChecklist,
  ChipStatusItem,
  DetalhesItemChecklist,
} from '@/components/documentos/ItemChecklistInfo'
import { SX_DESTAQUE, idItemChecklist, useRolarAteDestaque } from '@/hooks/useRolarAteDestaque'
import { useReleituraEmVerificacao } from '@/hooks/useReleituraEmVerificacao'
import { VersoesDocumento } from '@/components/documentos/VersoesDocumento'
import { ehConsultor } from '@/components/layout/menu'
import { authStore } from '@/store/authStore'
import { Documento } from '@/types/documento'
import { ChecklistReserva, ItemChecklistReserva } from '@/types/estruturacao'
import { formatDateTime } from '@/utils/formatters'

interface Props {
  reservaId: string
  /**
   * Avisa o pai a cada checklist carregado (após montar e após cada releitura) — é por aqui
   * que a página sabe se ainda falta obrigatório para liberar o envio. Sempre o checklist COMO
   * O BACKEND RESPONDEU: a tela não ajusta presença nem status.
   */
  onChecklist?: (checklist: ChecklistReserva) => void
  /**
   * Chave do item a abrir (deep link da Central de Pendências, Q40 — ex.
   * `/reservas/{id}/estruturacao?etapa=3&item=<chave>`): o item é rolado para a vista e
   * destacado. Sem a prop, lê `?item=` da URL atual.
   */
  itemDestacado?: string | null
  /**
   * Status da reserva, quando o pai conhece: fora de RASCUNHO/COM_PENDENCIA o consultor não troca
   * arquivo (a tela explica que a Originei abre a pendência). Sem ele, o backend responde (409).
   */
  statusReserva?: string | null
}

/** `?item=` da URL atual — fallback do deep link quando o pai não repassa a chave. */
const itemDaUrl = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('item')
  } catch {
    return null
  }
}

/** Rótulos dos OBRIGATÓRIOS que o backend diz não entregues (`presente`) — opcional nunca segura o envio. */
export const obrigatoriosPendentes = (checklist: ChecklistReserva | null): string[] =>
  (checklist?.categorias ?? [])
    .flatMap((g) => g.itens)
    .filter((i) => i.obrigatorio && !i.presente)
    .map(rotuloDoItem)

const ACEITOS = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.png'

/** Mesma identidade de item do `agruparArquivosPorItem` (chave ou rótulo legado). */
const chaveDe = (item: ItemChecklistReserva) => item.chave ?? `rotulo:${item.label}`

/** Item cujo arquivo deve ser trocado: recusado, vencido ou com falha de leitura. */
const pedeTroca = (item: ItemChecklistReserva): boolean => {
  const status = statusDoItem(item)
  return (
    status === 'RECUSADO' ||
    status === 'VENCIDO' ||
    (status === 'QUARENTENA' && (item.situacaoArquivos ?? []).includes('ERRO'))
  )
}

/**
 * Obrigatórios com arquivo enviado mas ainda "Em verificação" (leitura automática em curso): não
 * contam como entregues no servidor, então seguram o envio — a tela precisa dizer que é espera,
 * não arquivo faltando (Jornada Consultor 30/10, item 5). Falha de leitura fica de fora: pede troca.
 */
export const obrigatoriosEmVerificacao = (checklist: ChecklistReserva | null): string[] =>
  (checklist?.categorias ?? [])
    .flatMap((g) => g.itens)
    .filter((i) => i.obrigatorio && !i.presente && statusDoItem(i) === 'QUARENTENA' && !pedeTroca(i))
    .map(rotuloDoItem)

/**
 * Checklist documental da reserva com upload por item (obrigatórios, opcionais e solicitações da
 * Curadoria), pela versão PRESA à reserva (P3) e agrupado pelas categorias dela. Serve ao cadastro
 * da reserva (etapa 3 de Nova oportunidade) e a qualquer tela que precise completar documentos.
 *
 * Status por item vem do backend (Faltando · Em verificação · Recebido · Validado · Recusado ·
 * Vencido), com motivo da recusa, "Válido até" e "Reaproveitado" — nunca o autor da decisão nem a
 * origem do reuso. O arquivo novo passa por "Em verificação" até ser lido; enquanto houver, a tela
 * relê o checklist (5 s, até 2 min). Upload de item `sol-N` leva `exigenciaChave = sol-N`.
 *
 * Cada item mostra o(s) arquivo(s) com "Trocar" e "Excluir" (VC-16). Quem decide se o consultor
 * pode mexer é o backend (409 com o motivo — inclusive `DOCUMENTO_VALIDADO`); a tela só esconde
 * os botões do arquivo de outra pessoa, de item já validado e fora da janela da reserva.
 *
 * Reuso: "Reaproveitar documentos básicos" pede ao backend o reuso automático da própria carteira
 * (resposta só com a quantidade). O "Reaproveitar" por item (Q42) continua: o servidor devolve só
 * marcadores, sem nome, data ou autor da origem.
 */
export const ChecklistDocumentosReserva: FC<Props> = ({ reservaId, onChecklist, itemDestacado, statusReserva }) => {
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const consultor = ehConsultor(usuario?.permissoes)
  const [checklist, setChecklist] = useState<ChecklistReserva | null>(null)
  const [documentos, setDocumentos] = useState<Documento[]>([])
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando')
  const [uploadingChave, setUploadingChave] = useState<string | null>(null)
  const [ocupadoId, setOcupadoId] = useState<string | null>(null)
  const [reaproveitandoChave, setReaproveitandoChave] = useState<string | null>(null)
  const [reaproveitandoBasicos, setReaproveitandoBasicos] = useState(false)
  /** Tipos com documento elegível para reaproveitamento nesta reserva (só o tipo; nada da origem). */
  const [tiposElegiveis, setTiposElegiveis] = useState<Set<string>>(new Set())
  const [excluindo, setExcluindo] = useState<{ doc: Documento; item: ItemChecklistReserva } | null>(null)
  /**
   * Itens com arquivo enviado NESTA sessão que o backend ainda diz FALTANDO (a projeção chega
   * por evento). Só muda o TEXTO de apoio ("aguardando o registro") — nunca o status nem a contagem.
   */
  const [recemEnviados, setRecemEnviados] = useState<Set<string>>(new Set())
  const temporizadores = useRef<ReturnType<typeof setTimeout>[]>([])

  const carregarDocumentos = useCallback(async (): Promise<Documento[] | null> => {
    try {
      const page = await documentoService.listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, reservaId, 0, 500)
      const lista = page?.content ?? []
      setDocumentos(lista)
      return lista
    } catch {
      // Sem a lista o checklist continua funcionando — só não mostra os arquivos.
      return null
    }
  }, [reservaId])

  /** Elegíveis ao reuso: falha aqui não atrapalha o checklist — só não oferece "Reaproveitar". */
  const carregarElegiveis = useCallback(async () => {
    try {
      const lista = await documentoReusoService.elegiveis(reservaId)
      setTiposElegiveis(new Set((lista ?? []).filter((e) => e.marcador).map((e) => e.tipo)))
    } catch {
      setTiposElegiveis(new Set())
    }
  }, [reservaId])

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setEstado('carregando')
    try {
      const [c] = await Promise.all([
        estruturacaoConsultorService.checklist(reservaId),
        carregarDocumentos(),
        carregarElegiveis(),
      ])
      setChecklist(c)
      setEstado('ok')
      onChecklist?.(c)
    } catch {
      if (!silencioso) setEstado('erro')
    }
    // `onChecklist` muda a cada render do pai; incluí-lo recarregaria em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservaId, carregarDocumentos, carregarElegiveis])

  useEffect(() => {
    void carregar()
  }, [carregar])

  useEffect(
    () => () => {
      temporizadores.current.forEach(clearTimeout)
    },
    []
  )

  /** Re-consulta algumas vezes para trazer o estado oficial depois do evento do upload. */
  const reconsultar = () => {
    void carregar(true)
    for (const atraso of [1500, 4000, 8000]) {
      temporizadores.current.push(setTimeout(() => void carregar(true), atraso))
    }
  }

  // Enquanto houver arquivo sendo lido ("Em verificação"), relê a cada 5 s (até 2 min).
  useReleituraEmVerificacao(estado === 'ok' && temItemEmVerificacao(checklist), () => void carregar(true))

  /** Item do deep link (prop ou `?item=`), lido uma vez por reserva. */
  const destaque = useMemo(
    () => (itemDestacado !== undefined ? itemDestacado : itemDaUrl()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemDestacado, reservaId]
  )
  const grupos = useMemo(() => gruposComItens(checklist), [checklist])
  // Mesmo mecanismo dos demais destinos da Central (DEV-D): id `checklist-item-<chave|rótulo>`
  // + `useRolarAteDestaque` + `SX_DESTAQUE` — igual à etapa Documentos da Estruturação.
  const alvoExiste = !!destaque && grupos.some((g) => g.itens.some((i) => idItemChecklist(i) === destaque))
  useRolarAteDestaque(alvoExiste ? `checklist-item-${destaque}` : null, estado === 'ok' && !!checklist)

  const pendentes = useMemo(() => obrigatoriosPendentes(checklist), [checklist])
  const { porItem } = useMemo(() => agruparArquivosPorItem(checklist, documentos), [checklist, documentos])
  const naJanela = statusReserva == null || STATUS_JANELA_CONSULTOR.includes(statusReserva)

  /**
   * O consultor só mexe no que ele mesmo enviou, dentro da janela da reserva e nunca em item já
   * validado (só o back-office invalida). Back-office, em qualquer arquivo da reserva.
   */
  const podeAlterar = (doc: Documento, item: ItemChecklistReserva) =>
    !consultor ||
    ((!doc.usuarioUpload || !usuario?.id || doc.usuarioUpload === usuario.id) &&
      naJanela &&
      statusDoItem(item) !== 'VALIDADO')

  const marcarEnviado = (item: ItemChecklistReserva) =>
    setRecemEnviados((prev) => new Set(prev).add(chaveDe(item)))

  const handleUpload = async (item: ItemChecklistReserva, file: File) => {
    const rotulo = rotuloDoItem(item)
    setUploadingChave(chaveDe(item))
    try {
      await documentoService.upload(
        file,
        item.tipo ?? 'OUTRO',
        reservaId,
        TIPO_ENTIDADE_OPORTUNIDADE,
        // Qual exigência este arquivo satisfaz (V45) — inclusive `sol-N`: sem a chave o backend
        // cai no tipo, e upload sem chave nunca satisfaz solicitação da Curadoria.
        item.chave
      )
      enqueueSnackbar(`${rotulo} enviado — em verificação`, { variant: 'success' })
      marcarEnviado(item)
      reconsultar()
    } catch (e) {
      enqueueSnackbar((e as { message?: string })?.message || `Falha ao enviar ${rotulo}`, { variant: 'error' })
    } finally {
      setUploadingChave(null)
    }
  }

  /** Reuso automático dos básicos da própria carteira (P3 §8) — resposta só com a quantidade. */
  const handleReaproveitarBasicos = async () => {
    setReaproveitandoBasicos(true)
    try {
      const { solicitados } = await checklistReservaService.reaproveitarBasicos(reservaId)
      enqueueSnackbar(
        solicitados > 0
          ? `${solicitados} documento(s) básico(s) em reaproveitamento — o checklist atualiza em instantes.`
          : 'Nenhum documento básico validado da sua carteira está disponível para reaproveitar.',
        { variant: solicitados > 0 ? 'success' : 'info' }
      )
      reconsultar()
    } catch (e) {
      enqueueSnackbar(mensagemErroChecklistReserva(e, 'Não foi possível reaproveitar os documentos básicos.'), {
        variant: 'error',
      })
    } finally {
      setReaproveitandoBasicos(false)
    }
  }

  /**
   * Reaproveitar (Q42): o servidor devolve só marcadores; o do tipo do item volta para o
   * servidor, que confere tudo de novo e grava o documento nesta reserva.
   */
  const handleReaproveitar = async (item: ItemChecklistReserva) => {
    const rotulo = rotuloDoItem(item)
    setReaproveitandoChave(chaveDe(item))
    try {
      const elegiveis = item.tipo ? await documentoReusoService.elegiveis(reservaId, [item.tipo]) : []
      const oferta = elegiveis.find((e) => e.tipo === item.tipo && e.marcador)
      if (!oferta?.marcador) {
        enqueueSnackbar(MENSAGEM_POR_CODIGO.REUSO_NAO_AUTORIZADO, { variant: 'warning' })
        return
      }
      const novo = await documentoReusoService.reaproveitar({
        marcador: oferta.marcador,
        reservaId,
        exigenciaChave: item.chave,
      })
      // O reuso é idempotente por (origem, reserva): num segundo item do mesmo tipo o servidor
      // devolve o documento já vinculado ao primeiro — esse item continua pendente.
      if ((novo?.exigenciaChave ?? null) === (item.chave ?? null)) {
        enqueueSnackbar(`${rotulo} reaproveitado`, { variant: 'success' })
        marcarEnviado(item)
      } else {
        enqueueSnackbar(
          `Este documento já foi reaproveitado em outro item desta reserva. Envie um novo arquivo para ${rotulo}.`,
          { variant: 'warning' }
        )
      }
      void carregarElegiveis()
      reconsultar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, MENSAGEM_POR_CODIGO.REUSO_NAO_AUTORIZADO), { variant: 'error' })
    } finally {
      setReaproveitandoChave(null)
    }
  }

  const handleTrocar = async (item: ItemChecklistReserva, doc: Documento, file: File) => {
    const rotulo = rotuloDoItem(item)
    setOcupadoId(doc.id)
    try {
      await documentoService.substituir(doc.id, file)
      enqueueSnackbar(`Arquivo de ${rotulo} trocado`, { variant: 'success' })
      reconsultar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, `Não foi possível trocar o arquivo de ${rotulo}`), {
        variant: 'error',
      })
    } finally {
      setOcupadoId(null)
    }
  }

  const confirmarExclusao = async () => {
    if (!excluindo) return
    const { doc, item } = excluindo
    setOcupadoId(doc.id)
    try {
      await documentoService.remover(doc.id)
      enqueueSnackbar(`${doc.nome} excluído`, { variant: 'success' })
      setRecemEnviados((prev) => {
        const novo = new Set(prev)
        novo.delete(chaveDe(item))
        return novo
      })
      setDocumentos((prev) => prev.filter((d) => d.id !== doc.id))
      setExcluindo(null)
      reconsultar()
    } catch (e) {
      enqueueSnackbar(mensagemErroDocumento(e, `Não foi possível excluir ${doc.nome}`), { variant: 'error' })
      setExcluindo(null)
    } finally {
      setOcupadoId(null)
    }
  }

  if (estado === 'carregando' && !checklist) return <EstadoDeLista estado="carregando" />
  if (estado === 'erro' || !checklist) {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar o checklist de documentos"
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
      />
    )
  }

  return (
    <Box>
      <CabecalhoVersaoChecklist versao={checklist.versao} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={pendentes.length === 0 ? 'success' : 'warning'}
          label={
            checklist.total === 0
              ? 'Sem documentos exigidos'
              : pendentes.length === 0
                ? 'Documentos obrigatórios completos'
                : `${pendentes.length} obrigatório(s) pendente(s)`
          }
        />
        {checklist.total > 0 && (
          <Typography variant="caption" color="text.secondary">
            {checklist.entregues ?? checklist.presentes} de {checklist.total} obrigatórios enviados
            {checklist.validados != null ? ` · ${checklist.validados} validados pela Originei` : ''}
          </Typography>
        )}
        {temBasicoFaltando(checklist) && (
          <Button
            size="small"
            onClick={() => void handleReaproveitarBasicos()}
            disabled={reaproveitandoBasicos}
            startIcon={reaproveitandoBasicos ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
          >
            Reaproveitar documentos básicos
          </Button>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 2 }}>
        Formatos aceitos: PDF, Word, Excel, JPG ou PNG. Tamanho máximo de 25 MB por arquivo.
        Itens marcados como opcionais não seguram o envio.
      </Typography>

      {checklist.total === 0 && grupos.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Não há documentos exigidos para o produto desta reserva. Ela pode ser enviada
          para aprovação agora.
        </Alert>
      )}

      {grupos.map((grupo, indice) => (
        <Box key={chaveDoGrupo(grupo, indice)} sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            component="div"
            sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', mb: 1 }}
          >
            {rotuloDoGrupo(grupo)}
          </Typography>
          <Box>
            {grupo.itens.map((item) => {
              const status = statusDoItem(item)
              const rotulo = rotuloDoItem(item)
              const enviandoItem = uploadingChave === chaveDe(item)
              const arquivos = porItem.get(chaveDe(item)) ?? []
              const faltando = status === 'FALTANDO'
              // Pendente aqui, com documento do mesmo tipo elegível no servidor (outra reserva do CNPJ).
              const elegivelReuso = faltando && arquivos.length === 0 && !!item.tipo && tiposElegiveis.has(item.tipo)
              const reaproveitandoItem = reaproveitandoChave === chaveDe(item)
              const destacado = !!destaque && idItemChecklist(item) === destaque
              const aguardandoRegistro = faltando && recemEnviados.has(chaveDe(item))
              return (
                <Box
                  key={chaveDe(item)}
                  role="group"
                  aria-label={`Item ${rotulo}`}
                  id={`checklist-item-${idItemChecklist(item)}`}
                  aria-current={destacado ? 'true' : undefined}
                  data-destacado={destacado ? 'true' : undefined}
                  data-item-chave={item.chave ?? chaveDe(item)}
                  data-status={status}
                  sx={{
                    border: '1px solid',
                    borderColor:
                      status === 'VALIDADO'
                        ? 'success.main'
                        : status === 'RECUSADO' || status === 'VENCIDO'
                          ? 'error.main'
                          : 'divider',
                    borderRadius: 1.5,
                    mb: 1,
                    px: 2,
                    py: 1,
                    ...(destacado ? SX_DESTAQUE : {}),
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                      {status === 'VALIDADO' || status === 'RECEBIDO' ? (
                        <CheckCircleIcon color={status === 'VALIDADO' ? 'success' : 'info'} />
                      ) : status === 'RECUSADO' || status === 'VENCIDO' ? (
                        <ErrorOutlineIcon color="error" />
                      ) : (
                        <InsertDriveFileIcon color="disabled" />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {rotulo}
                          </Typography>
                          <ChipStatusItem status={status} />
                          {item.obrigatorio ? (
                            <Chip size="small" color="warning" variant="outlined" label="Obrigatório" />
                          ) : (
                            <Tooltip title="Desejável: não bloqueia o envio da reserva">
                              <Chip size="small" variant="outlined" label="Opcional" />
                            </Tooltip>
                          )}
                          {ehSolicitacaoCuradoria(item) && (
                            <Chip size="small" variant="outlined" color="secondary" label="Solicitado pela Curadoria" />
                          )}
                          {elegivelReuso && (
                            <Tooltip title="Documentos básicos não são solicitados novamente">
                              <Chip size="small" color="info" label="Elegível para reaproveitamento" />
                            </Tooltip>
                          )}
                        </Stack>
                        {faltando && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            {aguardandoRegistro
                              ? 'Arquivo enviado — aguardando o registro (atualiza em instantes)'
                              : elegivelReuso
                                ? 'Documento elegível para reaproveitamento'
                                : 'Pendente — aguardando upload'}
                          </Typography>
                        )}
                        {/* Consultor: nunca o autor da decisão nem a origem do reuso. */}
                        <DetalhesItemChecklist item={item} mostrarAutor={!consultor} />
                        {pedeTroca(item) && consultor && !naJanela && (
                          <Typography variant="caption" color="text.secondary" component="div">
                            Para trocar este arquivo, a Originei vai abrir uma pendência nesta reserva.
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    {elegivelReuso && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={reaproveitandoItem}
                        aria-label={`Reaproveitar ${rotulo}`}
                        onClick={() => void handleReaproveitar(item)}
                        startIcon={reaproveitandoItem ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                      >
                        Reaproveitar
                      </Button>
                    )}
                    {faltando && (
                      <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        disabled={enviandoItem}
                        startIcon={enviandoItem ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon />}
                      >
                        Enviar
                        <input
                          type="file"
                          hidden
                          aria-label={`Enviar ${rotulo}`}
                          accept={ACEITOS}
                          onChange={(e) => {
                            const f = e.target.files?.[0]
                            if (f) void handleUpload(item, f)
                            e.target.value = ''
                          }}
                        />
                      </Button>
                    )}
                  </Stack>

                  {arquivos.length > 0 && (
                    <List dense disablePadding aria-label={`Arquivos de ${rotulo}`} sx={{ mt: 1, pl: { sm: 4.5 } }}>
                      {arquivos.map((doc) => {
                        const ocupado = ocupadoId === doc.id
                        const alteravel = podeAlterar(doc, item)
                        return (
                          <ListItem
                            key={doc.id}
                            disableGutters
                            sx={{ flexWrap: 'wrap', gap: 1, borderTop: '1px dashed', borderColor: 'divider', py: 0.5 }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <InsertDriveFileIcon fontSize="small" color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.nome}
                              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, sx: { wordBreak: 'break-all' } }}
                              secondary={
                                <>
                                  {`Enviado em ${formatDateTime(doc.dataUpload ?? doc.createdAt)}`}
                                  {/* Vigente + trilha de versões (L11/Q37). */}
                                  <VersoesDocumento documento={doc} />
                                </>
                              }
                              secondaryTypographyProps={{ fontSize: '0.75rem', component: 'div' }}
                              sx={{ flex: '1 1 160px', my: 0 }}
                            />
                            {alteravel && (
                              <Stack direction="row" spacing={0.5}>
                                <Button
                                  component="label"
                                  size="small"
                                  disabled={ocupado}
                                  startIcon={ocupado ? <CircularProgress size={14} color="inherit" /> : <SwapHorizIcon />}
                                >
                                  Trocar
                                  <input
                                    type="file"
                                    hidden
                                    aria-label={`Trocar ${doc.nome}`}
                                    accept={ACEITOS}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0]
                                      if (f) void handleTrocar(item, doc, f)
                                      e.target.value = ''
                                    }}
                                  />
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  disabled={ocupado}
                                  startIcon={<DeleteOutlineIcon />}
                                  aria-label={`Excluir ${doc.nome}`}
                                  onClick={() => setExcluindo({ doc, item })}
                                >
                                  Excluir
                                </Button>
                              </Stack>
                            )}
                          </ListItem>
                        )
                      })}
                    </List>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      ))}

      <ConfirmDialog
        open={excluindo != null}
        title="Excluir documento"
        message={
          excluindo
            ? `Excluir "${excluindo.doc.nome}" de ${rotuloDoItem(excluindo.item)}? O item volta a ficar pendente se não houver outro arquivo.`
            : ''
        }
        confirmLabel="Excluir"
        isDangerous
        isLoading={excluindo != null && ocupadoId === excluindo.doc.id}
        onConfirm={() => void confirmarExclusao()}
        onCancel={() => setExcluindo(null)}
      />
    </Box>
  )
}

export default ChecklistDocumentosReserva
